'use client';

import { c as _c } from "react/compiler-runtime";
import Image, { ImageProps } from 'next/image';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import cloudflareLoader from '@/lib/imageLoader';

// Global optimize patterns configuration
let globalOptimizePatterns: string[] | undefined;

/**
 * Set global optimize patterns for all ImageLoader instances
 * Call this once during app initialization (e.g., in _app.tsx)
 *
 * @param patterns - Array of URL patterns to match for optimization
 *
 * @example
 * ```tsx
 * // In _app.tsx or app initialization
 * import { setGlobalOptimizePatterns } from '@/components/ImageLoader';
 *
 * setGlobalOptimizePatterns(['cdn.shopify.com', 'mycdn.com']);
 * ```
 */
export function setGlobalOptimizePatterns(patterns: string[] | undefined) {
  globalOptimizePatterns = patterns;
}

/**
 * Get current global optimize patterns
 *
 * @returns Current global patterns or undefined
 */
export function getGlobalOptimizePatterns(): string[] | undefined {
  return globalOptimizePatterns;
}
interface ImageLoaderProps extends Omit<ImageProps, 'loader'> {
  fallback?: React.ReactNode;
  aspectRatio?: string;
  loading?: 'lazy' | 'eager';
  optimizePatterns?: string[];
}

/**
 * ImageLoader component that conditionally uses Next.js Image with Cloudflare optimization
 *
 * Features:
 * - Automatic image optimization via Cloudflare (for matching URLs)
 * - Lazy loading by default (loads when near viewport)
 * - Loading states with placeholder
 * - Error handling with fallback UI
 * - Aspect ratio support
 * - Conditional optimization based on URL patterns
 * - Global pattern configuration
 *
 * @example
 * ```tsx
 * // Set global patterns once in _app.tsx
 * setGlobalOptimizePatterns(['cdn.shopify.com', 'mycdn.com']);
 *
 * // Then use ImageLoader without specifying patterns
 * <ImageLoader
 *   src="https://cdn.shopify.com/image.jpg"
 *   alt="Product"
 *   width={400}
 *   height={300}
 * />
 *
 * // Override global patterns for specific images
 * <ImageLoader
 *   src="/local-image.jpg"
 *   alt="Local"
 *   width={400}
 *   height={300}
 *   optimizePatterns={['mycdn.com']}
 * />
 * ```
 */
export function ImageLoader(t0) {
  const $ = _c(58);
  let alt;
  let aspectRatio;
  let className;
  let fallback;
  let height;
  let onError;
  let onLoad;
  let optimizePatterns;
  let props;
  let src;
  let style;
  let t1;
  let t2;
  let width;
  if ($[0] !== t0) {
    ({
      src,
      alt,
      fallback,
      aspectRatio,
      className,
      style,
      onLoad,
      onError,
      loading: t1,
      priority: t2,
      optimizePatterns,
      width,
      height,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = aspectRatio;
    $[3] = className;
    $[4] = fallback;
    $[5] = height;
    $[6] = onError;
    $[7] = onLoad;
    $[8] = optimizePatterns;
    $[9] = props;
    $[10] = src;
    $[11] = style;
    $[12] = t1;
    $[13] = t2;
    $[14] = width;
  } else {
    alt = $[1];
    aspectRatio = $[2];
    className = $[3];
    fallback = $[4];
    height = $[5];
    onError = $[6];
    onLoad = $[7];
    optimizePatterns = $[8];
    props = $[9];
    src = $[10];
    style = $[11];
    t1 = $[12];
    t2 = $[13];
    width = $[14];
  }
  const loading = t1 === undefined ? "lazy" : t1;
  const priority = t2 === undefined ? false : t2;
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  let t3;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const img = imgRef.current;
      if (!img) {
        return;
      }
      if (img.complete && img.naturalWidth > 0) {
        setIsLoaded(true);
      } else {
        if (img.complete && img.naturalWidth === 0) {
          setHasError(true);
        }
      }
    };
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== src) {
    t4 = [src];
    $[16] = src;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  useEffect(t3, t4);
  let t5;
  if ($[18] !== aspectRatio || $[19] !== style) {
    t5 = {
      aspectRatio,
      ...style
    };
    $[18] = aspectRatio;
    $[19] = style;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const containerStyle = t5;
  let t6;
  if ($[21] !== onLoad) {
    t6 = e => {
      setIsLoaded(true);
      setHasError(false);
      onLoad?.(e);
    };
    $[21] = onLoad;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const handleImageLoad = t6;
  let t7;
  if ($[23] !== onError) {
    t7 = e_0 => {
      setHasError(true);
      setIsLoaded(false);
      onError?.(e_0);
    };
    $[23] = onError;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const handleImageError = t7;
  const patternsToUse = optimizePatterns ?? globalOptimizePatterns;
  let t8;
  if ($[25] !== patternsToUse || $[26] !== src) {
    t8 = !patternsToUse || patternsToUse.some(pattern => {
      if (typeof src === "string") {
        return src.includes(pattern);
      }
      return false;
    });
    $[25] = patternsToUse;
    $[26] = src;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const shouldOptimize = t8;
  if (hasError && fallback) {
    let t9;
    if ($[28] !== className || $[29] !== containerStyle || $[30] !== fallback) {
      t9 = <div className={className} style={containerStyle}>{fallback}</div>;
      $[28] = className;
      $[29] = containerStyle;
      $[30] = fallback;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    return t9;
  }
  let t9;
  if ($[32] !== className) {
    t9 = clsx("relative overflow-hidden", className);
    $[32] = className;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== hasError || $[35] !== isLoaded) {
    t10 = !isLoaded && !hasError && <div className="absolute inset-0 bg-gray-200 animate-pulse" />;
    $[34] = hasError;
    $[35] = isLoaded;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== alt || $[38] !== handleImageError || $[39] !== handleImageLoad || $[40] !== height || $[41] !== isLoaded || $[42] !== loading || $[43] !== priority || $[44] !== props || $[45] !== shouldOptimize || $[46] !== src || $[47] !== width) {
    t11 = shouldOptimize ? <Image ref={imgRef} src={src} alt={alt} fill={true} sizes={`${width}px`} loader={cloudflareLoader} loading={priority ? "eager" : loading} priority={priority} className={clsx("transition-opacity duration-300", isLoaded ? "opacity-100" : "opacity-0")} style={{
      objectFit: "cover"
    }} onLoad={handleImageLoad} onError={handleImageError} {...props} /> : <img ref={imgRef} src={typeof src === "string" ? src : (src as {
      src: string;
    }).src} alt={alt} width={width} height={height} loading={priority ? "eager" : loading} className={clsx("absolute inset-0 w-full h-full transition-opacity duration-300", isLoaded ? "opacity-100" : "opacity-0")} style={{
      objectFit: "cover"
    }} onLoad={handleImageLoad} onError={handleImageError} />;
    $[37] = alt;
    $[38] = handleImageError;
    $[39] = handleImageLoad;
    $[40] = height;
    $[41] = isLoaded;
    $[42] = loading;
    $[43] = priority;
    $[44] = props;
    $[45] = shouldOptimize;
    $[46] = src;
    $[47] = width;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[49] !== fallback || $[50] !== hasError) {
    t12 = hasError && !fallback && <div className="absolute inset-0 flex items-center justify-center bg-gray-100"><div className="text-center p-4"><svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><p className="text-xs text-gray-500">Image unavailable</p></div></div>;
    $[49] = fallback;
    $[50] = hasError;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== containerStyle || $[53] !== t10 || $[54] !== t11 || $[55] !== t12 || $[56] !== t9) {
    t13 = <div className={t9} style={containerStyle}>{t10}{t11}{t12}</div>;
    $[52] = containerStyle;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
    $[56] = t9;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  return t13;
}